import {
  FIELD_TYPES,
  FIELD_TYPE_CATALOGUE,
  fieldFormatSchema,
  formatFieldValue,
  type FieldType,
  type FieldVisibility,
  type LayoutEntity,
  type ValueTone,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { FieldCell } from '../components/fields/cell.tsx';
import { LayoutEditor } from '../components/fields/layout-editor.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Badge, type BadgeVariant } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { describeError } from '../lib/api.ts';
import {
  api,
  type FieldDefinitionInput,
  type FieldDefinitionView,
  type RoleView,
} from '../lib/client.ts';
import { fieldsQuery } from '../lib/custom-fields.ts';
import { useSession } from '../lib/session.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { Textarea } from '../components/ui/input.tsx';
import { nativeSelectClass } from '../components/ui/input.tsx';
import { cn } from '../lib/utils.ts';

/**
 * Settings → Fields (RELAY-941): the admin defines custom fields here and they are live at
 * once — the API loads the active definitions per request, so the candidates table, filters,
 * forms, export and the per-organisation OpenAPI schema pick the new field up without a deploy.
 * Since TS-1752 the page switches between record kinds, each with its own fields and layout.
 */
export const FIELDS_QUERY = fieldsQuery('candidates');

/**
 * The kinds the page manages: every one carries custom fields and a layout. Today the two
 * contract lists are the same; were they to part, `api.fieldDefinitions.list(entity)` and
 * `toInput` stop type-checking, which is the prompt to narrow this.
 */
type FieldsEntity = LayoutEntity;
const ENTITIES: ReadonlyArray<{
  value: FieldsEntity;
  label: string;
  description: string;
  /**
   * Known gap (TS-1752, TS-1840): the app's create flows for this kind ask for no custom
   * fields, so ticking Required makes them fail with a 400. Absent where the form asks.
   */
  requiredGap?: string;
}> = [
  {
    value: 'candidates',
    label: 'Candidates',
    description:
      'Custom fields on candidates. A field is live the moment it is saved: in the table, filters, forms, export and the API.',
  },
  {
    value: 'companies',
    label: 'Companies',
    description:
      'Custom fields on companies. A field is live in the API the moment it is saved (values, filters, history); the app will show them on the company page.',
    requiredGap:
      '“New project” doesn’t ask for custom fields yet, so a required company field will make creating a project with a new company fail.',
  },
  {
    value: 'contacts',
    label: 'Contacts',
    description:
      'Custom fields on contacts. A field is live in the API the moment it is saved (values, filters, history); the app does not show contact values yet.',
    requiredGap:
      '“New contact” on a project doesn’t ask for custom fields yet, so a required contact field will make adding a new contact fail.',
  },
  {
    value: 'projects',
    label: 'Projects',
    description:
      "Custom fields on projects. A field is live the moment it is saved: on the project page's Overview, in the API and in filters.",
    requiredGap:
      '“New project” doesn’t ask for custom fields yet, so a required project field will make creating a project fail.',
  },
  {
    value: 'deals',
    label: 'Deals',
    description:
      'Custom fields on deals. A field is live in the API the moment it is saved (values, filters, history); the app does not show deal values yet. Deal fields are never sent to RecruitCRM.',
    requiredGap:
      '“Add deal” doesn’t ask for custom fields yet, so a required deal field will make adding a deal fail.',
  },
  {
    value: 'jobs',
    label: 'Jobs',
    description:
      'Custom fields on jobs. A field is live in the API the moment it is saved (values, filters, history); the app does not show job values yet.',
    requiredGap:
      '“Add job” doesn’t ask for custom fields yet, so a required job field will make adding a job fail.',
  },
];

interface Draft {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  isIndexed: boolean;
  group: string;
  position: number;
  description: string;
  /** "value:Label" per line. */
  optionsText: string;
  /** Role key → hidden | read; a role not listed edits (TS-956). */
  visibility: Record<string, FieldVisibility>;
  prefix: string;
  suffix: string;
  /** '' = as typed. */
  decimals: string;
  /** Colour ranges in order; the last row is "Above" and its `upTo` is ignored. */
  bands: Array<{ upTo: string; tone: ValueTone }>;
}
const EMPTY: Draft = {
  key: '',
  label: '',
  type: 'text',
  required: false,
  isIndexed: true,
  group: '',
  position: 0,
  description: '',
  optionsText: '',
  visibility: {},
  prefix: '',
  suffix: '',
  decimals: '',
  bands: [],
};

// The renderers `FieldCell` applies a format to; select, boolean, date and link cells ignore it.
const takesFormat = (type: FieldType) =>
  ['text', 'long_text', 'phone', 'number', 'currency'].includes(
    FIELD_TYPE_CATALOGUE[type].cellRenderer,
  );
const takesDecimals = (type: FieldType) => type === 'number' || type === 'currency';
// Ranges compare a plain number; a currency value is cents plus a code, so it takes none.
const takesBands = (type: FieldType) => type === 'number';

type Format = NonNullable<FieldDefinitionInput['format']>;

const MAX_BANDS = 6;

function draftBands(d: Draft): NonNullable<Format['bands']> {
  return d.bands.map((b, i) =>
    i === d.bands.length - 1
      ? { tone: b.tone }
      : { upTo: b.upTo.trim() === '' ? NaN : Number(b.upTo), tone: b.tone },
  );
}

/** Null when the ranges are valid (or none); else why Save is off. */
function bandsError(d: Draft): string | null {
  if (!takesBands(d.type) || d.bands.length === 0) return null;
  return fieldFormatSchema.shape.bands.safeParse(draftBands(d)).success
    ? null
    : 'Each “Up to” needs a number, larger than the one above it.';
}

function draftFormat(d: Draft): Format | null {
  if (!takesFormat(d.type)) return null;
  const format: Format = {
    ...(d.prefix ? { prefix: d.prefix } : {}),
    ...(d.suffix ? { suffix: d.suffix } : {}),
    ...(takesDecimals(d.type) && d.decimals !== '' ? { decimals: Number(d.decimals) } : {}),
    ...(takesBands(d.type) && d.bands.length && !bandsError(d) ? { bands: draftBands(d) } : {}),
  };
  return Object.keys(format).length ? format : null;
}

const FORMAT_SAMPLE: Partial<Record<FieldType, unknown>> = {
  number: 1234.5,
  currency: { amountMinor: 123450, currency: 'USD' },
};

function toInput(d: Draft, entity: FieldsEntity): FieldDefinitionInput {
  const spec = FIELD_TYPE_CATALOGUE[d.type];
  const options = spec.hasOptions
    ? d.optionsText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const [value, label] = l.split(':');
          return { value: value!.trim(), label: (label ?? value)!.trim() };
        })
    : undefined;
  return {
    entity,
    key: d.key,
    label: d.label,
    type: d.type,
    required: d.required,
    isIndexed: d.isIndexed,
    position: d.position,
    // `edit` is the default, so only the exceptions are stored.
    visibility: Object.fromEntries(Object.entries(d.visibility).filter(([, v]) => v !== 'edit')),
    // null clears a stored format on edit.
    format: draftFormat(d),
    ...(d.group ? { group: d.group } : {}),
    ...(d.description ? { description: d.description } : {}),
    ...(options ? { options } : {}),
  };
}

function toDraft(f: FieldDefinitionView): Draft {
  return {
    key: f.key,
    label: f.label,
    type: f.type,
    required: f.required,
    isIndexed: f.isIndexed,
    group: f.group ?? '',
    position: f.position,
    description: f.description ?? '',
    optionsText: (f.options ?? []).map((o) => `${o.value}:${o.label}`).join('\n'),
    visibility: { ...f.visibility },
    prefix: f.format?.prefix ?? '',
    suffix: f.format?.suffix ?? '',
    decimals: f.format?.decimals === undefined ? '' : String(f.format.decimals),
    bands: (f.format?.bands ?? []).map((b) => ({
      upTo: b.upTo === undefined ? '' : String(b.upTo),
      tone: b.tone,
    })),
  };
}

export function FieldsPage() {
  const session = useSession();
  const qc = useQueryClient();
  const [entity, setEntity] = useState<FieldsEntity>('candidates');
  const current = ENTITIES.find((e) => e.value === entity)!;
  const defs = useQuery({
    queryKey: fieldsQuery(entity),
    queryFn: () => api.fieldDefinitions.list(entity),
  });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canManage = session.can('field_definition.manage');
  const roles = useQuery({
    queryKey: ['roles', session.me.organisation.id],
    queryFn: api.roles.list,
    enabled: canManage && session.can('role.read'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: fieldsQuery(entity) });

  const save = useMutation({
    mutationFn: async (e: { id: string | null; draft: Draft }) => {
      const input = toInput(e.draft, entity);
      if (e.id) {
        const { entity: _e, key: _k, type: _t, ...patch } = input;
        return api.fieldDefinitions.patch(e.id, patch);
      }
      return api.fieldDefinitions.create(input);
    },
    onSuccess: async () => {
      setEditing(null);
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.fieldDefinitions.remove(id),
    onSuccess: refresh,
    onError: (err) => setError(describeError(err)),
  });

  return (
    <Gate permission="field_definition.read" feature="custom_fields.enabled">
      <PageHeader
        title="Fields"
        description={current.description}
        actions={
          canManage && (
            <Button onClick={() => setEditing({ id: null, draft: EMPTY })} data-testid="new-field">
              <Plus /> New field
            </Button>
          )
        }
      />
      <Tabs
        value={entity}
        onValueChange={(v) => {
          setEntity(v as FieldsEntity);
          setEditing(null);
          setError(null);
        }}
        className="mb-4"
      >
        <TabsList aria-label="Record kind">
          {ENTITIES.map((e) => (
            <TabsTrigger key={e.value} value={e.value} data-testid={`fields-entity-${e.value}`}>
              {e.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {error && (
        <p className="text-danger mb-3 text-sm" role="alert">
          {error}
        </p>
      )}
      {defs.isError && <p className="text-danger text-sm">{describeError(defs.error)}</p>}
      <div className="bg-card shadow-card rounded-card overflow-x-auto border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-muted-foreground bg-muted text-left text-xs">
            <tr>
              <th className="px-4 py-2.5 font-medium">Label</th>
              <th className="px-4 py-2.5 font-medium">Key</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Group</th>
              <th className="px-4 py-2.5 font-medium">Flags</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {(defs.data ?? []).map((f) => (
              <tr
                key={f.id}
                className="border-border hover:bg-surface-hover/60 border-t transition-colors"
                data-testid={`field-${f.key}`}
              >
                <td className="px-4 py-3 font-medium">{f.label}</td>
                <td className="px-4 py-3">
                  <code className="text-xs">{f.key}</code>
                </td>
                <td className="px-4 py-3">{FIELD_TYPE_CATALOGUE[f.type].label}</td>
                <td className="text-muted-foreground px-4 py-3">{f.group ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className="flex flex-wrap gap-1">
                    {f.required && <Badge variant="warning">required</Badge>}
                    {f.isIndexed && <Badge variant="outline">filterable</Badge>}
                    {Object.values(f.visibility).some((v) => v === 'hidden') && (
                      <Badge variant="secondary">
                        hidden from{' '}
                        {Object.values(f.visibility).filter((v) => v === 'hidden').length}
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <span className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ id: f.id, draft: toDraft(f) })}
                        aria-label={`Edit ${f.label}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove.mutate(f.id)}
                        aria-label={`Retire ${f.label}`}
                      >
                        Retire
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {defs.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="text-muted-foreground py-8 text-center">
                  No custom fields yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <LayoutEditor
        key={entity}
        entity={entity}
        definitions={defs.data ?? []}
        canManage={canManage}
      />
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          {editing && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(editing);
              }}
              className="flex flex-col gap-3"
            >
              <DialogHeader>
                <DialogTitle>
                  {editing.id ? `Edit ${editing.draft.label}` : 'New field'}
                </DialogTitle>
              </DialogHeader>
              <FieldRow label="Label">
                <Input
                  aria-label="Label"
                  required
                  value={editing.draft.label}
                  onChange={(e) =>
                    setEditing({ ...editing, draft: { ...editing.draft, label: e.target.value } })
                  }
                />
              </FieldRow>
              <FieldRow label="Key" hint="snake_case; cannot change later">
                <Input
                  aria-label="Key"
                  required
                  pattern="[a-z][a-z0-9_]{0,63}"
                  disabled={editing.id !== null}
                  value={editing.draft.key}
                  onChange={(e) =>
                    setEditing({ ...editing, draft: { ...editing.draft, key: e.target.value } })
                  }
                />
              </FieldRow>
              <FieldRow label="Type" hint="cannot change later">
                <Select
                  value={editing.draft.type}
                  onValueChange={(v) =>
                    setEditing({ ...editing, draft: { ...editing.draft, type: v as FieldType } })
                  }
                  disabled={editing.id !== null}
                >
                  <SelectTrigger aria-label="Type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {FIELD_TYPE_CATALOGUE[t].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              {FIELD_TYPE_CATALOGUE[editing.draft.type].hasOptions && (
                <FieldRow label="Options" hint="one per line: value:Label">
                  <Textarea
                    aria-label="Options"
                    value={editing.draft.optionsText}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        draft: { ...editing.draft, optionsText: e.target.value },
                      })
                    }
                    className="min-h-20"
                  />
                </FieldRow>
              )}
              <FieldRow label="Group" hint="section in the form">
                <Input
                  aria-label="Group"
                  value={editing.draft.group}
                  onChange={(e) =>
                    setEditing({ ...editing, draft: { ...editing.draft, group: e.target.value } })
                  }
                />
              </FieldRow>
              <FieldRow label="Position">
                <Input
                  aria-label="Position"
                  type="number"
                  min={0}
                  value={editing.draft.position}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      draft: { ...editing.draft, position: Number(e.target.value) },
                    })
                  }
                />
              </FieldRow>
              {takesFormat(editing.draft.type) && (
                <fieldset className="flex flex-col gap-2" data-testid="display-format">
                  <legend className="text-sm font-medium">
                    Display format{' '}
                    <span className="text-muted-foreground font-normal">
                      · how lists and filters show the value
                    </span>
                  </legend>
                  <div className="grid grid-cols-3 gap-2">
                    <FieldRow label="Prefix">
                      <Input
                        aria-label="Prefix"
                        maxLength={8}
                        placeholder="$"
                        value={editing.draft.prefix}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            draft: { ...editing.draft, prefix: e.target.value },
                          })
                        }
                      />
                    </FieldRow>
                    <FieldRow label="Suffix">
                      <Input
                        aria-label="Suffix"
                        maxLength={8}
                        placeholder=" hrs"
                        value={editing.draft.suffix}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            draft: { ...editing.draft, suffix: e.target.value },
                          })
                        }
                      />
                    </FieldRow>
                    {takesDecimals(editing.draft.type) && (
                      <FieldRow label="Decimals">
                        <Input
                          aria-label="Decimals"
                          type="number"
                          min={0}
                          max={6}
                          value={editing.draft.decimals}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              draft: { ...editing.draft, decimals: e.target.value },
                            })
                          }
                        />
                      </FieldRow>
                    )}
                  </div>
                  {takesBands(editing.draft.type) && (
                    <BandsEditor
                      draft={editing.draft}
                      onChange={(bands) =>
                        setEditing({ ...editing, draft: { ...editing.draft, bands } })
                      }
                    />
                  )}
                  <p className="text-muted-foreground text-sm" data-testid="format-preview">
                    e.g.{' '}
                    <FieldCell
                      type={editing.draft.type}
                      value={FORMAT_SAMPLE[editing.draft.type] ?? 'Sample'}
                      format={draftFormat(editing.draft)}
                    />
                  </p>
                </fieldset>
              )}
              <div className="flex gap-6 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editing.draft.required}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        draft: { ...editing.draft, required: e.target.checked },
                      })
                    }
                  />{' '}
                  Required
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editing.draft.isIndexed}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        draft: { ...editing.draft, isIndexed: e.target.checked },
                      })
                    }
                  />{' '}
                  Filterable and sortable
                </label>
              </div>
              {editing.draft.required && current.requiredGap && (
                <p
                  className="text-state-warning-fg text-sm"
                  role="note"
                  data-testid="required-warning"
                >
                  {current.requiredGap}
                </p>
              )}
              {roles.data && (
                <VisibilityEditor
                  roles={roles.data}
                  value={editing.draft.visibility}
                  onChange={(visibility) =>
                    setEditing({ ...editing, draft: { ...editing.draft, visibility } })
                  }
                />
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={save.isPending || bandsError(editing.draft) !== null}
                  data-testid="save-field"
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

const TONE_LABEL: Record<ValueTone, string> = {
  positive: 'Green',
  caution: 'Yellow',
  negative: 'Red',
};
const TONE_BADGE: Record<ValueTone, BadgeVariant> = {
  positive: 'success',
  caution: 'caution',
  negative: 'danger',
};

/** Colour ranges of a number: "Up to N → tone" rows in order, then "Above → tone". */
function BandsEditor({
  draft,
  onChange,
}: {
  draft: Draft;
  onChange: (bands: Draft['bands']) => void;
}) {
  const rows = draft.bands;
  const set = (i: number, patch: Partial<Draft['bands'][number]>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () =>
    onChange(
      rows.length
        ? [...rows.slice(0, -1), { upTo: '', tone: 'caution' }, rows.at(-1)!]
        : [
            { upTo: '', tone: 'negative' },
            { upTo: '', tone: 'positive' },
          ],
    );
  // Removing the last "Up to" leaves "Above" alone, which colours nothing apart: clear it all.
  const remove = (i: number) => onChange(rows.length <= 2 ? [] : rows.filter((_, j) => j !== i));
  const error = bandsError(draft);
  const format = draftFormat(draft);
  const toneSelect = (i: number, label: string) => (
    <select
      aria-label={label}
      value={rows[i]!.tone}
      onChange={(e) => set(i, { tone: e.target.value as ValueTone })}
      className={cn(nativeSelectClass, 'h-9 w-28')}
    >
      {(Object.keys(TONE_LABEL) as ValueTone[]).map((t) => (
        <option key={t} value={t}>
          {TONE_LABEL[t]}
        </option>
      ))}
    </select>
  );
  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="color-ranges">
      <span className="font-medium">
        Color ranges{' '}
        <span className="text-muted-foreground font-normal">· color the value in lists</span>
      </span>
      {rows.map((r, i) =>
        i === rows.length - 1 ? (
          <div key={i} className="flex items-center gap-2">
            <span className="w-16">Above</span>
            <span className="w-28" />
            <span aria-hidden>→</span>
            {toneSelect(i, 'Above color')}
          </div>
        ) : (
          <div key={i} className="flex items-center gap-2">
            <span className="w-16">Up to</span>
            <Input
              aria-label={`Range ${i + 1} up to`}
              type="number"
              step="any"
              value={r.upTo}
              onChange={(e) => set(i, { upTo: e.target.value })}
              className="h-9 w-28"
            />
            <span aria-hidden>→</span>
            {toneSelect(i, `Range ${i + 1} color`)}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => remove(i)}
              aria-label={`Remove range ${i + 1}`}
            >
              <X />
            </Button>
          </div>
        ),
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      {rows.length < MAX_BANDS && (
        <Button type="button" size="sm" variant="outline" className="self-start" onClick={add}>
          <Plus /> Add range
        </Button>
      )}
      {format?.bands && (
        <p className="text-muted-foreground" data-testid="bands-preview">
          e.g.{' '}
          {format.bands.map((b, i) => {
            const prev = format.bands![i - 1]?.upTo;
            const sample = b.upTo ?? (prev === undefined ? 0 : prev + 1);
            return (
              <Badge key={i} variant={TONE_BADGE[b.tone]} className="mr-2" data-tone={b.tone}>
                {formatFieldValue(sample, format)}
              </Badge>
            );
          })}
        </p>
      )}
    </div>
  );
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {label} {hint && <span className="text-muted-foreground font-normal">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const VISIBILITY_LABEL: Record<FieldVisibility, string> = {
  edit: 'Can edit',
  read: 'Read only',
  hidden: 'Hidden',
};

/**
 * Per-role visibility of a field (TS-956). The API enforces it on every read and write; a role
 * that manages fields always edits (so nobody hides a field from the people who configure it),
 * and the Integration role is an API key's, so neither is offered here.
 */
function VisibilityEditor({
  roles,
  value,
  onChange,
}: {
  roles: readonly RoleView[];
  value: Record<string, FieldVisibility>;
  onChange: (next: Record<string, FieldVisibility>) => void;
}) {
  const managesFields = (r: RoleView) =>
    r.grants.some((g) => g.permission === 'field_definition.manage') ||
    (r.inherits !== null &&
      roles.some(
        (p) =>
          p.key === r.inherits && p.grants.some((g) => g.permission === 'field_definition.manage'),
      ));
  const shown = roles.filter((r) => r.key !== 'integration');
  return (
    <fieldset
      className="border-border flex flex-col gap-2 rounded-xl border p-4"
      data-testid="field-visibility"
    >
      <legend className="px-1 text-sm font-semibold">Who can see it</legend>
      {shown.map((r) => {
        const fixed = managesFields(r);
        return (
          <div
            key={r.key}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm"
          >
            <span className="font-medium">{r.name}</span>
            {fixed ? (
              <span className="text-muted-foreground text-xs">Always edits (manages fields)</span>
            ) : (
              <select
                aria-label={`${r.name} visibility`}
                value={value[r.key] ?? 'edit'}
                onChange={(e) => onChange({ ...value, [r.key]: e.target.value as FieldVisibility })}
                className={cn(nativeSelectClass, 'h-9')}
              >
                {(['edit', 'read', 'hidden'] as const).map((v) => (
                  <option key={v} value={v}>
                    {VISIBILITY_LABEL[v]}
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
